import { IManagedUserRecentSummary, IManagerProduct, IManagerProductOption, IUser } from '@/types';
import { ISortConfig, ITableColumn } from '@/components/Tables';
import { MANAGER_LABELS } from '@/constants';
import { useAppLoading } from '@/hooks';
import { TManagerDashboardFilter } from './types';
import { handleModalUnlockConfirm as handleUnlockConfirm } from './utils/handle-modal-unlock-confirm';
import { useGlobalStore } from '@/store';
import { useManagerDashboardMemoData } from './hooks/useManagerDashboardMemoData';
import { useManagerDashboardProductFiltering } from './hooks/useManagerDashboardProductFiltering';
import { useManagerDashboardSorting } from './hooks/useManagerDashboardSorting';
import { useManagerDashboardTable } from './hooks/useManagerDashboardTable';
import { useCallback, useEffect, useState } from 'react';

/** Return type for the `useManagerDashboardFeature` hook */
interface UseManagerDashboardFeatureReturn {
  /** The currently active filter applied to the dashboard */
  activeFilter: TManagerDashboardFilter;
  /** The currently logged-in user */
  currentUser: IUser | null;
  /** Text describing the current filter results */
  filteredResultsText: string;
  /** Users after filtering and sorting */
  filteredUsers: IManagedUserRecentSummary[];
  /** Callback to clear the active filter */
  handleFilterClear: () => void;
  /** Callback to apply a summary-card filter */
  handleFilterSelect: (filter: TManagerDashboardFilter) => void;
  /** Callback when the unlock modal cancel button is clicked */
  handleModalUnlockCancel: () => void;
  /** Callback when the unlock modal confirm button is clicked */
  handleModalUnlockConfirm: () => Promise<void>;
  /** Error displayed in unlock modal when unlock request is denied */
  unlockModalError: string | null;
  /** Callback when the product dropdown changes */
  handleProductChange: (value: string) => void;
  /** Callback when a sortable column header is clicked */
  handleSort: (key: string, direction: ISortConfig['direction']) => void;
  /** Loading state for the dashboard */
  isLoading: boolean;
  /** All managed users for the active product (pre-filter) */
  managedUsers: IManagedUserRecentSummary[];
  /** The currently selected certification product */
  managerActiveProduct: IManagerProduct | null;
  /** Options for the certification product dropdown */
  productOptions: IManagerProductOption[];
  /** Whether to show the filter results text */
  showFilterResultsText: boolean;
  /** Current table sort configuration */
  sortConfig: ISortConfig;
  /** Table column definitions */
  tableColumns: ITableColumn<IManagedUserRecentSummary>[];
  /** Users who have completed certification */
  usersCertified: IManagedUserRecentSummary[];
  /** Users who need attention (e.g. failed certification, passed practice but not cert) */
  usersNeedingAttention: IManagedUserRecentSummary[];
  /** Users who have passed practice but not yet certified */
  usersPassedPractice: IManagedUserRecentSummary[];
  /** Whether the unlock certification modal is open */
  unlockModalOpen: boolean;
  /** The user/scenario currently selected for unlocking (if any) */
  selectedUserForUnlock: ReturnType<typeof useManagerDashboardTable>['selectedUserForUnlock'];
}

/**
 * Feature hook that encapsulates all state, effects, handlers, and derived values
 * required by the Manager Dashboard view.
 *
 * Why this hook exists:
 * - Keeps the `ManagerDashboard` component render-focused and easier to scan.
 * - Centralizes orchestration logic (store + local state + async actions).
 * - Composes smaller hooks (`table`, `sorting`, `filtering`, `memo-data`) behind one API.
 * - Reduces prop-drilling and spreads dashboard-specific behavior into a clear feature boundary.
 *
 * Architectural notes:
 * - Global state comes from `useGlobalStore` (current user, active product, team summary).
 * - Local UI state (active filter, filtered rows, sort config) is managed here.
 * - Domain logic is delegated to feature-level hooks under `./hooks`.
 * - Unlock action side effects are contained here to keep the UI component declarative.
 */
export const useManagerDashboardFeature = (): UseManagerDashboardFeatureReturn => {
  // ================================
  // Global store selectors/actions
  // ================================
  // These values/actions are cross-feature app state and are intentionally read from the store.
  const {
    currentUser,
    managerActiveProduct,
    managersTeamSummary,
    setManagerActiveProduct,
    setManagerActiveUserIsLoading,
  } = useGlobalStore();

  // Global app-loading signal used to render skeleton/loading UI consistently.
  const isLoading = useAppLoading();

  // ================================
  // Table behavior and modal state
  // ================================
  // Encapsulates table column definitions and unlock-modal UI state.
  const {
    tableColumns,
    unlockModalOpen,
    setUnlockModalOpen,
    selectedUserForUnlock,
    setSelectedUserForUnlock,
  } = useManagerDashboardTable();

  // ================================
  // Local dashboard state
  // ================================
  // `activeFilter`: currently selected summary-card filter (or null for all users).
  const [activeFilter, setActiveFilter] = useState<TManagerDashboardFilter>(null);

  // `filteredUsers`: table rows after applying product selection + filter + sort.
  const [filteredUsers, setFilteredUsers] = useState<IManagedUserRecentSummary[]>([]);

  // `sortConfig`: current table sort key/direction.
  // Default key remains stable while direction starts as unsorted (`null`).
  const [sortConfig, setSortConfig] = useState<ISortConfig>({
    key: 'certificationStatus',
    direction: null,
  });

  const [unlockModalError, setUnlockModalError] = useState<string | null>(null);

  // ================================
  // Bootstrapping effects
  // ================================
  // On mount/sync, ensure managed-user loading is not left in a stale "true" state
  // when arriving at the manager dashboard view.
  useEffect(() => {
    setManagerActiveUserIsLoading(false);
  }, [setManagerActiveUserIsLoading]);

  // ========================================
  // Active product initialization
  // =======================================
  // Initialize active product the first time team summary becomes available.
  // This guarantees the dashboard has a selected certification context and initial rows.
  useEffect(() => {
    if (!managerActiveProduct && managersTeamSummary?.certifications?.length) {
      const [firstCertification] = managersTeamSummary.certifications;
      setManagerActiveProduct(firstCertification);
      setFilteredUsers(firstCertification.managedUsers || []);
      setActiveFilter(null);
    }
  }, [managerActiveProduct, managersTeamSummary, setManagerActiveProduct]);

  // ================================
  // Derived memoized data
  // ================================
  // Produces:
  // - `managedUsers` for the active product
  // - prefiltered user subsets for summary cards
  // - select options and filter-results UI text values
  const {
    filteredResultsText,
    managedUsers,
    productOptions,
    showFilterResultsText,
    usersCertified,
    usersNeedingAttention,
    usersPassedPractice,
  } = useManagerDashboardMemoData({
    activeFilter,
    filteredUsers,
    managersTeamSummary,
    managerActiveProduct,
    sortConfig,
  });

  // ================================
  // Filtering and sorting actions
  // ================================
  // Filter toggles are delegated to a dedicated hook to keep this file focused on orchestration.
  const { handleFilterSelect } = useManagerDashboardProductFiltering({
    activeFilter,
    managedUsers,
    managerActiveProduct,
    usersCertified,
    usersNeedingAttention,
    usersPassedPractice,
    setActiveFilter,
    setFilteredUsers,
  });

  // Table sort behavior is delegated for testability and reuse.
  const { handleSort } = useManagerDashboardSorting({
    filteredUsers,
    setFilteredUsers,
    setSortConfig,
  });

  // ================================
  // Product selection action
  // ================================
  // Keep table rows in sync whenever the active-product user list changes.
  // This happens, for example, when product selection changes or summary data refreshes.
  useEffect(() => {
    setFilteredUsers(managedUsers);
  }, [managedUsers]);

  // ================================
  // Unlock modal actions
  // ================================
  /**
   * Confirms unlock action for the selected user/scenario.
   *
   * Guards:
   * - active product must exist
   * - selected user must exist
   * - scenario + user ids must both be present
   *
   * Side effects:
   * - Calls unlock API
   * - Closes modal and clears selected user on success (HTTP 200)
   */
  const handleModalUnlockConfirm = useCallback(async () => {
    if (!selectedUserForUnlock) return;

    try {
      setUnlockModalError(null);

      const result = await handleUnlockConfirm({
        currentUser,
        managedUsers,
        managerActiveProduct,
        selectedUserId: selectedUserForUnlock.userId,
        selectedScenarioId: selectedUserForUnlock.scenarioId || null,
      });

      if (result.success) {
        setUnlockModalOpen(false);
        setSelectedUserForUnlock(null);
        setUnlockModalError(null);
      } else {
        const reasonKey = result.reason as keyof typeof MANAGER_LABELS;
        setUnlockModalError(MANAGER_LABELS[reasonKey] || MANAGER_LABELS.UNLOCK_DENIAL_API_FAILED);
      }
    } catch (error) {
      setUnlockModalError(MANAGER_LABELS.UNLOCK_DENIAL_API_FAILED);
      console.error('Error unlocking certification attempts:', error);
    }
  }, [
    currentUser,
    managedUsers,
    managerActiveProduct,
    selectedUserForUnlock,
    setUnlockModalError,
    setUnlockModalOpen,
    setSelectedUserForUnlock,
  ]);

  /**
   * Cancels unlock flow by closing modal and clearing selected user context.
   */
  const handleModalUnlockCancel = useCallback(() => {
    setUnlockModalError(null);
    setUnlockModalOpen(false);
    setSelectedUserForUnlock(null);
  }, [setUnlockModalError, setUnlockModalOpen, setSelectedUserForUnlock]);

  // ================================
  // Product/filter utility actions
  // ================================
  /**
   * Handles certification product selection from the dropdown.
   *
   * Behavior:
   * - Locates selected certification from team summary
   * - Updates global active product
   * - Resets table data to selected product's full user list
   * - Clears any active summary filter
   */
  const handleProductChange = useCallback(
    (value: string): void => {
      const selectedCertification = managersTeamSummary?.certifications.find(
        (cert) => cert.certificationId.toString() === value,
      );

      if (!selectedCertification) {
        console.warn('Selected certification not found for value:', value);
        return;
      }

      setManagerActiveProduct(selectedCertification);
      setFilteredUsers(selectedCertification.managedUsers || []);
      setActiveFilter(null);
    },
    [managersTeamSummary, setManagerActiveProduct],
  );

  /**
   * Clears dashboard filters and sorting, returning to default table state.
   */
  const handleFilterClear = useCallback(() => {
    setActiveFilter(null);
    setFilteredUsers(managedUsers);
    setSortConfig({ key: 'certificationStatus', direction: null });
  }, [managedUsers]);

  // ================================
  // Feature API (returned contract)
  // ================================
  // This is the single object consumed by the `ManagerDashboard` component.
  // Keep this shape stable to minimize UI refactors.
  return {
    activeFilter,
    currentUser,
    filteredResultsText,
    filteredUsers,
    handleFilterClear,
    handleFilterSelect,
    handleModalUnlockCancel,
    handleModalUnlockConfirm,
    handleProductChange,
    handleSort,
    isLoading,
    managedUsers,
    managerActiveProduct,
    productOptions,
    selectedUserForUnlock,
    showFilterResultsText,
    sortConfig,
    tableColumns,
    unlockModalError,
    unlockModalOpen,
    usersCertified,
    usersNeedingAttention,
    usersPassedPractice,
  };
};
